import React from 'react';
import { Dropdown } from 'flowbite-react';
import { HiOutlineChevronDown, HiOutlineViewGrid } from 'react-icons/hi';
import { SiMicrosoftazure, SiJira, SiAsana } from 'react-icons/si';
import CustomButton from './CustomButton';

/**
 * Interface for the SourceDropdown component props
 */
interface SourceDropdownProps {
  sources: string[];
  selectedSources: string[];
  onToggleSource: (source: string) => void;
  className?: string;
}

/**
 * Monday.com icon component
 */
const MondayIcon = () => (
  <div className="w-4 h-4 flex-shrink-0 relative">
    <div className="absolute top-0 left-0 w-1.5 h-1.5 rounded-full bg-[#ff3d57]"></div>
    <div className="absolute top-0 right-0 w-1.5 h-1.5 rounded-full bg-[#ff3d57]"></div>
    <div className="absolute bottom-0 left-0 w-1.5 h-1.5 rounded-full bg-[#ff3d57]"></div>
    <div className="absolute bottom-0 right-0 w-1.5 h-1.5 rounded-full bg-[#ff3d57]"></div>
  </div>
);

/**
 * CadencePro icon component
 */
const CadenceProIcon = () => (
  <img 
    src="/candencepro.png" 
    alt="CadencePro Logo" 
    className="h-4 w-4"
  />
);

/**
 * Map API source name to display name
 */
const mapSourceToDisplayName = (source: string): string => {
  switch(source.toLowerCase()) {
    case 'azure': return 'Azure DevOps';
    case 'jira': return 'Jira';
    case 'asana': return 'Asana';
    case 'cadence': return 'CadencePro';
    case 'monday': return 'Monday';
    default: return source;
  }
};

/**
 * Get icon component for a source
 */
const getSourceIcon = (source: string) => {
  const lowerSource = source.toLowerCase();
  
  switch(lowerSource) {
    case 'azure':
      return <SiMicrosoftazure className="h-4 w-4 text-blue-600" />;
    case 'jira':
      return <SiJira className="h-4 w-4 text-blue-500" />;
    case 'asana':
      return <SiAsana className="h-4 w-4 text-red-500" />;
    case 'cadence':
      return <CadenceProIcon />;
    case 'monday':
      return <MondayIcon />;
    default:
      return <div className="h-4 w-4 flex items-center justify-center bg-indigo-500 rounded-full">
        <span className="text-xs font-bold text-white">{source.charAt(0).toUpperCase()}</span>
      </div>;
  }
};

/**
 * Dropdown component for selecting project sources
 */
const SourceDropdown: React.FC<SourceDropdownProps> = ({
  sources,
  selectedSources,
  onToggleSource,
  className = '',
}) => {
  // Normalize sources to ensure consistent casing and formatting
  const normalizedSources = sources.map(source => {
    return {
      original: source,
      display: mapSourceToDisplayName(source),
      key: source.toLowerCase()
    };
  });
  
  // // Add Monday if not already included
  // if (!normalizedSources.some(s => s.key === 'monday')) {
  //   normalizedSources.push({
  //     original: 'monday',
  //     display: 'Monday',
  //     key: 'monday'
  //   });
  // }
  
  // Sort sources alphabetically by display name
  normalizedSources.sort((a, b) => a.display.localeCompare(b.display));
  
  return (
    <div className={`flex items-center ${className}`}>
      <Dropdown
        label={
          <div className="flex items-center gap-1">
            <HiOutlineViewGrid className="mr-1" />
            Sources
          </div>
        }
        dismissOnClick={false}
        color="light"
        size="sm"
        className="bg-gray-700 dark:bg-gray-800 text-white"
      >
        {normalizedSources.map((source) => (
          <Dropdown.Item key={source.key} onClick={() => onToggleSource(source.original)}>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedSources.includes(source.original)}
                onChange={() => {}}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600"
                aria-label={`Toggle ${source.display}`}
              />
              <div className="flex items-center gap-1.5">
                {getSourceIcon(source.key)}
                <span>{source.display}</span>
              </div>
            </div>
          </Dropdown.Item>
        ))}
      </Dropdown>
    </div>
  );
};

export default SourceDropdown; 